/**
 * Universal panelis search (⌘K / Ctrl+K): orders, customers, products and
 * categories in one debounced query against /api/v1/admin/global-search.
 * Enter or click navigates straight to the record.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, ShoppingCart, User, FolderTree } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

type SearchData = {
  orders: Array<{
    id: string;
    order_number: string;
    customer_name: string | null;
    customer_email: string | null;
    status: string;
    total_amount: string | null;
  }>;
  customers: Array<{
    id: string;
    full_name: string | null;
    email: string | null;
    phone: string | null;
    company_name: string | null;
  }>;
  products: Array<{ id: string; title: string; sku: string | null; active: boolean }>;
  categories: Array<{ id: string; name: string; slug: string }>;
};

const EMPTY: SearchData = { orders: [], customers: [], products: [], categories: [] };

export function GlobalSearch() {
  const navigate = useNavigate();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [data, setData] = useState<SearchData>(EMPTY);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setData(EMPTY);
    }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setData(EMPTY);
      return;
    }
    const timer = setTimeout(async () => {
      // Guard against out-of-order responses: only the newest query wins.
      const mine = ++seq.current;
      setLoading(true);
      try {
        const res = await api<{ data: SearchData }>(
          `/api/v1/admin/global-search?q=${encodeURIComponent(q)}`,
        );
        if (mine === seq.current) setData(res.data ?? EMPTY);
      } catch {
        // keep previous results on a transient failure
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const go = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const hasResults =
    data.orders.length + data.customers.length + data.products.length + data.categories.length > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hidden h-8 w-56 items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 font-body text-sm text-muted-foreground transition-colors hover:border-primary/50 md:flex"
      >
        <Search size={14} />
        <span className="flex-1 text-left">{t("search.placeholder", "Meklēt…")}</span>
        <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-[10px]">
          ⌘K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl overflow-hidden p-0">
          <Command shouldFilter={false} className="[&_[cmdk-input-wrapper]]:border-b">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder={t(
                "search.inputPlaceholder",
                "Meklēt pasūtījumus, klientus, produktus…",
              )}
            />
            <CommandList className="max-h-96">
              {query.trim().length >= 2 && !loading && !hasResults && (
                <CommandEmpty>{t("search.empty", "Nekas netika atrasts.")}</CommandEmpty>
              )}

              {data.orders.length > 0 && (
                <CommandGroup heading={t("search.orders", "Pasūtījumi")}>
                  {data.orders.map((o) => (
                    <CommandItem
                      key={o.id}
                      value={`order-${o.id}`}
                      onSelect={() => go(`/orders/${o.id}`)}
                    >
                      <ShoppingCart size={14} className="mr-2 text-muted-foreground" />
                      <span className="mr-2 font-mono text-xs">{o.order_number}</span>
                      <span className="truncate">
                        {o.customer_name?.trim() || o.customer_email || "—"}
                      </span>
                      <span className="ml-auto text-xs capitalize text-muted-foreground">
                        {o.status}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {data.customers.length > 0 && (
                <CommandGroup heading={t("search.customers", "Klienti")}>
                  {data.customers.map((cu) => (
                    <CommandItem
                      key={cu.id}
                      value={`customer-${cu.id}`}
                      onSelect={() => go(`/customers/${cu.id}`)}
                    >
                      <User size={14} className="mr-2 text-muted-foreground" />
                      <span className="truncate">
                        {cu.full_name?.trim() || cu.company_name || cu.email || cu.phone}
                      </span>
                      {cu.full_name?.trim() && cu.email && (
                        <span className="ml-2 truncate text-xs text-muted-foreground">
                          {cu.email}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {data.products.length > 0 && (
                <CommandGroup heading={t("search.products", "Produkti")}>
                  {data.products.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`product-${p.id}`}
                      onSelect={() => go(`/products/${p.id}`)}
                    >
                      <Package size={14} className="mr-2 text-muted-foreground" />
                      <span className="truncate">{p.title}</span>
                      {p.sku && (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {p.sku}
                        </span>
                      )}
                      {!p.active && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          {t("search.inactive", "neaktīvs")}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {data.categories.length > 0 && (
                <CommandGroup heading={t("search.categories", "Kategorijas")}>
                  {data.categories.map((cat) => (
                    <CommandItem
                      key={cat.id}
                      value={`category-${cat.id}`}
                      onSelect={() => go(`/categories/${cat.id}`)}
                    >
                      <FolderTree size={14} className="mr-2 text-muted-foreground" />
                      <span className="truncate">{cat.name}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {cat.slug}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
